import * as React from "react";

import { sentryDataCollection } from "@repo/shared/telemetry/sentry";
import * as Sentry from "@sentry/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ConvexProvider } from "convex/react";
import ReactDOM from "react-dom/client";

import { publicConvexClient } from "@/lib/convex-clients";

import "@repo/ui/globals.css";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const convexNetworkUrls = [/^https:\/\/.*\.convex\.(cloud|site)/, /^http:\/\/127\.0\.0\.1:321[01]/];

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD && Boolean(import.meta.env.VITE_SENTRY_DSN),
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  initialScope: { tags: { app: "web" } },
  dataCollection: sentryDataCollection,
  traceLifecycle: "static",
  tracesSampleRate: 1,
  tracePropagationTargets: [/^\//],
  replaysSessionSampleRate: 1,
  replaysOnErrorSampleRate: 1,
  profileSessionSampleRate: 1,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration({
      blockAllMedia: false,
      maskAllText: false,
      networkDetailAllowUrls: convexNetworkUrls,
      networkRequestHeaders: ["Content-Type"],
      networkResponseHeaders: ["Content-Type"],
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
  ],
});

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ConvexProvider client={publicConvexClient}>
        <Sentry.ErrorBoundary fallback={<p className="p-6 text-sm">Something went wrong.</p>}>
          <RouterProvider router={router} />
        </Sentry.ErrorBoundary>
      </ConvexProvider>
    </React.StrictMode>,
  );
}
